import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminSection } from "./admin";
import { AdminButton, AdminDialog, Field, Input, Row, TableShell, Textarea } from "@/components/admin/AdminUI";

export const Route = createFileRoute("/_authenticated/admin/events")({
  component: EventsAdmin,
});

type E = Database["public"]["Tables"]["events"]["Row"];
type Ins = Database["public"]["Tables"]["events"]["Insert"];

const KINDS = ["live", "listening_party", "livestream", "workshop"] as const;

const EMPTY: Ins = {
  slug: "", title: "", kind: "live", description: "", starts_at: new Date().toISOString(),
  ends_at: null, timezone: "UTC", location: "", stream_url: "", rsvp_url: "", image_url: "",
  published: false, sort_order: 0,
};

function EventsAdmin() {
  const [rows, setRows] = useState<E[]>([]);
  const [editing, setEditing] = useState<E | Ins | null>(null);

  async function load() {
    const { data } = await supabase.from("events").select("*").order("starts_at", { ascending: false });
    setRows((data ?? []) as E[]);
  }
  useEffect(() => { load(); }, []);

  async function save(form: Ins) {
    const payload = { ...form, slug: form.slug.trim(), title: form.title.trim() };
    if ("id" in form && (form as E).id) {
      await supabase.from("events").update(payload).eq("id", (form as E).id);
    } else {
      await supabase.from("events").insert(payload);
    }
    setEditing(null);
    load();
  }

  async function togglePublish(row: E) {
    await supabase.from("events").update({ published: !row.published }).eq("id", row.id);
    load();
  }
  async function remove(row: E) {
    if (!confirm(`Delete "${row.title}"?`)) return;
    await supabase.from("events").delete().eq("id", row.id);
    load();
  }

  return (
    <div className="space-y-8">
      <AdminSection
        title="Events"
        description="Live performances, listening parties, livestreams, and workshops shown on the public events page."
        action={<AdminButton onClick={() => setEditing({ ...EMPTY })}>New event</AdminButton>}
      >
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events yet.</p>
        ) : (
          <TableShell
            headers={["Title", "Kind", "Starts", "Published", ""]}
            rows={rows.map((r) => (
              <tr key={r.id} className="border-t hairline">
                <td className="px-3 py-2 text-sm">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-muted-foreground">{r.slug}</div>
                </td>
                <td className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground">{r.kind}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {new Date(r.starts_at).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-xs">
                  <button onClick={() => togglePublish(r)} className={`rounded-full border hairline px-2 py-0.5 ${r.published ? "text-emerald-400" : "text-muted-foreground"}`}>
                    {r.published ? "Published" : "Draft"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <Row>
                    <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                    <AdminButton variant="danger" onClick={() => remove(r)}>Delete</AdminButton>
                  </Row>
                </td>
              </tr>
            ))}
          />
        )}
      </AdminSection>

      {editing && (
        <EventDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function EventDialog({ initial, onClose, onSave }: { initial: E | Ins; onClose: () => void; onSave: (v: Ins) => void }) {
  const [form, setForm] = useState<Ins>(initial as Ins);
  return (
    <AdminDialog open onClose={onClose} title={"id" in initial && (initial as E).id ? "Edit event" : "New event"}>
      <form
        onSubmit={(e) => { e.preventDefault(); onSave(form); }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Title">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </Field>
          <Field label="Slug" hint="URL-friendly identifier">
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
          </Field>
          <Field label="Kind">
            <select className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm"
              value={form.kind ?? "live"} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Timezone">
            <Input value={form.timezone ?? "UTC"} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          </Field>
          <Field label="Starts at (ISO)">
            <Input type="datetime-local" value={toLocal(form.starts_at)} onChange={(e) => setForm({ ...form, starts_at: new Date(e.target.value).toISOString() })} required />
          </Field>
          <Field label="Ends at (optional)">
            <Input type="datetime-local" value={toLocal(form.ends_at)} onChange={(e) => setForm({ ...form, ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
          </Field>
          <Field label="Location">
            <Input value={form.location ?? ""} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </Field>
          <Field label="Image URL">
            <Input value={form.image_url ?? ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          </Field>
          <Field label="RSVP URL">
            <Input value={form.rsvp_url ?? ""} onChange={(e) => setForm({ ...form, rsvp_url: e.target.value })} />
          </Field>
          <Field label="Stream URL">
            <Input value={form.stream_url ?? ""} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} />
          </Field>
        </div>
        <Field label="Description">
          <Textarea rows={5} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published ?? false} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
          Published
        </label>
        <Row>
          <AdminButton type="submit">Save</AdminButton>
          <AdminButton variant="ghost" type="button" onClick={onClose}>Cancel</AdminButton>
        </Row>
      </form>
    </AdminDialog>
  );
}

function toLocal(iso: string | null | undefined) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
